import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../../config';
import './SuperAdminDashboard.css';
import logo from '../../assets/Logo - Side.png';

function SuperAdminDashboard() {
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('overview');
  const [companies, setCompanies] = useState([]);
  const [subscriptions, setSubscriptions] = useState({});
  const [plans, setPlans] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [selectedCompany, setSelectedCompany] = useState(null);
  const [companyDetails, setCompanyDetails] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [companiesRes, plansRes] = await Promise.all([
        fetch(`${API_BASE_URL}/api/companies`),
        fetch(`${API_BASE_URL}/api/plans`)
      ]);

      if (companiesRes.ok) {
        const data = await companiesRes.json();
        const companyList = data.companies || [];
        setCompanies(companyList);
        
        const subs = {};
        for (const company of companyList) {
          try {
            const subRes = await fetch(`${API_BASE_URL}/api/saas/subscription?company_id=${company.id}`);
            if (subRes.ok) {
              const subData = await subRes.json();
              subs[company.id] = subData;
            }
          } catch (e) {}
        }
        setSubscriptions(subs);
        
        const totalCompanies = companyList.length;
        const activeCompanies = companyList.filter(c => c.is_active).length;
        const trialCompanies = Object.values(subs).filter(s => s?.subscription?.status === 'trialing').length;
        const paidCompanies = Object.values(subs).filter(s => s?.subscription?.status === 'active' && s?.subscription?.plan_slug !== 'free').length;
        
        setStats({ totalCompanies, activeCompanies, trialCompanies, paidCompanies });
      }

      if (plansRes.ok) {
        const data = await plansRes.json();
        setPlans(data.plans || []);
      }
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanyDetails = async (company) => {
    setSelectedCompany(company);
    const sub = subscriptions[company.id];
    setCompanyDetails({ company, ...sub });
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    return new Date(dateString).toLocaleDateString('en-US', { 
      month: 'short', day: 'numeric', year: 'numeric' 
    });
  };

  const filteredCompanies = companies.filter(c => 
    c.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    c.slug?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const getPlanColor = (planSlug) => {
    const colors = {
      free: { bg: '#f1f5f9', text: '#64748b' },
      starter: { bg: '#dbeafe', text: '#1d4ed8' },
      professional: { bg: '#f3e8ff', text: '#7c3aed' },
      enterprise: { bg: '#fef3c7', text: '#b45309' }
    };
    return colors[planSlug] || colors.free;
  };

  return (
    <div className="admin-dashboard">
      {/* Header */}
      <header className="admin-header">
        <div className="header-left">
          <img src={logo} alt="RentafAI" className="header-logo" />
          <div className="header-divider"></div>
          <span className="header-title">Admin Console</span>
        </div>
        <div className="header-right">
          <button className="header-btn" onClick={() => navigate('/')}>
            Exit Admin
          </button>
        </div>
      </header>

      <div className="admin-layout">
        {/* Sidebar */}
        <aside className="admin-sidebar">
          <nav className="sidebar-nav">
            <button 
              className={`nav-item ${activeTab === 'overview' ? 'active' : ''}`}
              onClick={() => setActiveTab('overview')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <rect x="3" y="3" width="7" height="7" rx="1"/>
                <rect x="14" y="3" width="7" height="7" rx="1"/>
                <rect x="3" y="14" width="7" height="7" rx="1"/>
                <rect x="14" y="14" width="7" height="7" rx="1"/>
              </svg>
              Overview
            </button>
            <button 
              className={`nav-item ${activeTab === 'companies' ? 'active' : ''}`}
              onClick={() => setActiveTab('companies')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M3 21h18"/>
                <path d="M5 21V7l8-4v18"/>
                <path d="M19 21V11l-6-4"/>
                <path d="M9 9v.01"/>
                <path d="M9 12v.01"/>
                <path d="M9 15v.01"/>
                <path d="M9 18v.01"/>
              </svg>
              Companies
            </button>
            <button 
              className={`nav-item ${activeTab === 'plans' ? 'active' : ''}`}
              onClick={() => setActiveTab('plans')}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <path d="M2 17l10 5 10-5"/>
                <path d="M2 12l10 5 10-5"/>
                <path d="M12 2L2 7l10 5 10-5-10-5z"/>
              </svg>
              Plans
            </button>
          </nav>
        </aside>

        {/* Main Content */}
        <main className="admin-main">
          {/* Overview Tab */}
          {activeTab === 'overview' && (
            <div className="admin-content">
              <div className="content-header">
                <h1>Dashboard Overview</h1>
                <p>Monitor your SaaS platform performance</p>
              </div>

              <div className="stats-row">
                <div className="stat-card">
                  <div className="stat-card-icon blue">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M3 21h18"/>
                      <path d="M5 21V7l8-4v18"/>
                      <path d="M19 21V11l-6-4"/>
                    </svg>
                  </div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{stats.totalCompanies || 0}</span>
                    <span className="stat-card-label">Total Companies</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-icon green">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                  </div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{stats.activeCompanies || 0}</span>
                    <span className="stat-card-label">Active</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-icon yellow">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="12" cy="12" r="10"/>
                      <polyline points="12 6 12 12 16 14"/>
                    </svg>
                  </div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{stats.trialCompanies || 0}</span>
                    <span className="stat-card-label">On Trial</span>
                  </div>
                </div>

                <div className="stat-card">
                  <div className="stat-card-icon purple">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <rect x="1" y="4" width="22" height="16" rx="2" ry="2"/>
                      <line x1="1" y1="10" x2="23" y2="10"/>
                    </svg>
                  </div>
                  <div className="stat-card-info">
                    <span className="stat-card-value">{stats.paidCompanies || 0}</span>
                    <span className="stat-card-label">Paid</span>
                  </div>
                </div>
              </div>

              {/* Recent Companies */}
              <div className="content-card">
                <div className="card-header">
                  <h2>Recent Companies</h2>
                  <button className="text-btn" onClick={() => setActiveTab('companies')}>
                    View All
                  </button>
                </div>
                <div className="company-list">
                  {loading ? (
                    <div className="loading-state">Loading...</div>
                  ) : companies.slice(0, 5).map(company => {
                    const sub = subscriptions[company.id]?.subscription;
                    return (
                      <div key={company.id} className="company-row" onClick={() => fetchCompanyDetails(company)}>
                        <div className="company-avatar" style={{ background: company.primary_color || '#1457f5' }}>
                          {company.name?.charAt(0).toUpperCase()}
                        </div>
                        <div className="company-info">
                          <span className="company-name">{company.name}</span>
                          <span className="company-email">{company.email || 'No email'}</span>
                        </div>
                        {sub && (
                          <span 
                            className="plan-tag"
                            style={{ 
                              background: getPlanColor(sub.plan_slug).bg,
                              color: getPlanColor(sub.plan_slug).text
                            }}
                          >
                            {sub.plan}
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          )}

          {/* Companies Tab */}
          {activeTab === 'companies' && (
            <div className="admin-content">
              <div className="content-header">
                <div>
                  <h1>Companies</h1>
                  <p>Manage all registered companies</p>
                </div>
                <div className="header-actions">
                  <div className="search-box">
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <circle cx="11" cy="11" r="8"/>
                      <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    <input 
                      type="text" 
                      placeholder="Search companies..." 
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                  </div>
                </div>
              </div>

              <div className="content-card">
                {loading ? (
                  <div className="loading-state">Loading companies...</div>
                ) : filteredCompanies.length === 0 ? (
                  <div className="empty-state">
                    <p>No companies found</p>
                  </div>
                ) : (
                  <div className="companies-grid">
                    {filteredCompanies.map(company => {
                      const sub = subscriptions[company.id]?.subscription;
                      const usage = subscriptions[company.id]?.usage;
                      return (
                        <div 
                          key={company.id} 
                          className="company-card"
                          onClick={() => fetchCompanyDetails(company)}
                        >
                          <div className="company-card-header">
                            <div className="company-avatar" style={{ background: company.primary_color || '#1457f5' }}>
                              {company.name?.charAt(0).toUpperCase()}
                            </div>
                            <div className="company-card-title">
                              <h3>{company.name}</h3>
                              <span>{company.slug}</span>
                            </div>
                          </div>
                          
                          <div className="company-card-body">
                            <div className="company-meta">
                              <span>{company.email || 'No email'}</span>
                            </div>
                            
                            {sub && (
                              <div className="company-plan-row">
                                <span 
                                  className="plan-tag"
                                  style={{ 
                                    background: getPlanColor(sub.plan_slug).bg,
                                    color: getPlanColor(sub.plan_slug).text
                                  }}
                                >
                                  {sub.plan}
                                </span>
                                <span className={`status-dot ${sub.status}`}></span>
                              </div>
                            )}

                            {usage && (
                              <div className="company-usage">
                                <div className="usage-stat">
                                  <span className="usage-num">{usage.properties || 0}</span>
                                  <span className="usage-label">Properties</span>
                                </div>
                                <div className="usage-stat">
                                  <span className="usage-num">{usage.units || 0}</span>
                                  <span className="usage-label">Units</span>
                                </div>
                                <div className="usage-stat">
                                  <span className="usage-num">{usage.users || 0}</span>
                                  <span className="usage-label">Users</span>
                                </div>
                              </div>
                            )}
                          </div>

                          <div className="company-card-footer">
                            <span className="created-date">Created {formatDate(company.created_at)}</span>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Plans Tab */}
          {activeTab === 'plans' && (
            <div className="admin-content">
              <div className="content-header">
                <h1>Subscription Plans</h1>
                <p>Manage pricing tiers and features</p>
              </div>

              <div className="plans-grid">
                {plans.map(plan => (
                  <div key={plan.id} className={`plan-card ${plan.is_featured ? 'featured' : ''}`}>
                    {plan.is_featured && <div className="featured-ribbon">Popular</div>}
                    <div className="plan-card-header">
                      <h3>{plan.name}</h3>
                      <p>{plan.description}</p>
                    </div>
                    <div className="plan-card-price">
                      <span className="price">${plan.price_monthly}</span>
                      <span className="period">/month</span>
                    </div>
                    {plan.price_yearly > 0 && (
                      <p className="yearly-note">${plan.price_yearly}/year (save ${plan.price_monthly * 12 - plan.price_yearly})</p>
                    )}
                    <div className="plan-card-limits">
                      <div className="limit-row">
                        <span className="limit-label">Properties</span>
                        <span className="limit-value">{plan.max_properties === -1 ? 'Unlimited' : plan.max_properties}</span>
                      </div>
                      <div className="limit-row">
                        <span className="limit-label">Units</span>
                        <span className="limit-value">{plan.max_units === -1 ? 'Unlimited' : plan.max_units}</span>
                      </div>
                      <div className="limit-row">
                        <span className="limit-label">Users</span>
                        <span className="limit-value">{plan.max_users === -1 ? 'Unlimited' : plan.max_users}</span>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </main>
      </div>

      {/* Company Detail Drawer */}
      {selectedCompany && (
        <>
          <div className="drawer-overlay" onClick={() => { setSelectedCompany(null); setCompanyDetails(null); }}></div>
          <div className="company-drawer">
            <div className="drawer-header">
              <div className="drawer-title">
                <div className="company-avatar lg" style={{ background: selectedCompany.primary_color || '#1457f5' }}>
                  {selectedCompany.name?.charAt(0).toUpperCase()}
                </div>
                <div>
                  <h2>{selectedCompany.name}</h2>
                  <span>{selectedCompany.slug}</span>
                </div>
              </div>
              <button className="drawer-close" onClick={() => { setSelectedCompany(null); setCompanyDetails(null); }}>
                <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <line x1="18" y1="6" x2="6" y2="18"/>
                  <line x1="6" y1="6" x2="18" y2="18"/>
                </svg>
              </button>
            </div>

            <div className="drawer-body">
              {companyDetails?.subscription && (
                <div className="drawer-section">
                  <h3>Subscription</h3>
                  <div className="subscription-display">
                    <div className="sub-plan">
                      <span 
                        className="plan-tag lg"
                        style={{ 
                          background: getPlanColor(companyDetails.subscription.plan_slug).bg,
                          color: getPlanColor(companyDetails.subscription.plan_slug).text
                        }}
                      >
                        {companyDetails.subscription.plan}
                      </span>
                      <span className={`status-badge ${companyDetails.subscription.status}`}>
                        {companyDetails.subscription.status}
                      </span>
                    </div>
                    <div className="sub-details">
                      <p>${companyDetails.subscription.price?.monthly}/month</p>
                      {companyDetails.subscription.trial_ends_at && (
                        <p className="sub-date">Trial ends {formatDate(companyDetails.subscription.trial_ends_at)}</p>
                      )}
                    </div>
                  </div>
                </div>
              )}

              {companyDetails?.usage && (
                <div className="drawer-section">
                  <h3>Usage</h3>
                  <div className="usage-bars">
                    <div className="usage-bar-item">
                      <div className="usage-bar-header">
                        <span>Properties</span>
                        <span>{companyDetails.usage.properties} / {companyDetails.subscription?.limits?.max_properties === -1 ? '∞' : companyDetails.subscription?.limits?.max_properties}</span>
                      </div>
                      <div className="usage-bar">
                        <div 
                          className="usage-bar-fill"
                          style={{ 
                            width: companyDetails.subscription?.limits?.max_properties === -1 
                              ? '10%' 
                              : `${Math.min((companyDetails.usage.properties / companyDetails.subscription?.limits?.max_properties) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="usage-bar-item">
                      <div className="usage-bar-header">
                        <span>Units</span>
                        <span>{companyDetails.usage.units} / {companyDetails.subscription?.limits?.max_units === -1 ? '∞' : companyDetails.subscription?.limits?.max_units}</span>
                      </div>
                      <div className="usage-bar">
                        <div 
                          className="usage-bar-fill"
                          style={{ 
                            width: companyDetails.subscription?.limits?.max_units === -1 
                              ? '10%' 
                              : `${Math.min((companyDetails.usage.units / companyDetails.subscription?.limits?.max_units) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                    <div className="usage-bar-item">
                      <div className="usage-bar-header">
                        <span>Users</span>
                        <span>{companyDetails.usage.users} / {companyDetails.subscription?.limits?.max_users === -1 ? '∞' : companyDetails.subscription?.limits?.max_users}</span>
                      </div>
                      <div className="usage-bar">
                        <div 
                          className="usage-bar-fill"
                          style={{ 
                            width: companyDetails.subscription?.limits?.max_users === -1 
                              ? '10%' 
                              : `${Math.min((companyDetails.usage.users / companyDetails.subscription?.limits?.max_users) * 100, 100)}%` 
                          }}
                        ></div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              <div className="drawer-section">
                <h3>Contact</h3>
                <div className="info-grid">
                  <div className="info-item">
                    <label>Email</label>
                    <span>{selectedCompany.email || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Phone</label>
                    <span>{selectedCompany.phone || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Website</label>
                    <span>{selectedCompany.website || '-'}</span>
                  </div>
                  <div className="info-item">
                    <label>Address</label>
                    <span>
                      {selectedCompany.address 
                        ? `${selectedCompany.address}, ${selectedCompany.city || ''} ${selectedCompany.state || ''}`
                        : '-'}
                    </span>
                  </div>
                </div>
              </div>

              <div className="drawer-section">
                <h3>Branding</h3>
                <div className="branding-preview">
                  <div 
                    className="color-swatch"
                    style={{ background: selectedCompany.primary_color || '#1457f5' }}
                  >
                    Primary
                  </div>
                  <div 
                    className="color-swatch"
                    style={{ background: selectedCompany.secondary_color || '#60aaff' }}
                  >
                    Secondary
                  </div>
                </div>
              </div>
            </div>

            <div className="drawer-footer">
              <button className="btn-secondary" onClick={() => { setSelectedCompany(null); setCompanyDetails(null); }}>
                Close
              </button>
              <button 
                className="btn-primary"
                onClick={() => {
                  localStorage.setItem('adminViewCompany', JSON.stringify(selectedCompany));
                  navigate('/dashboard');
                }}
              >
                View Dashboard
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

export default SuperAdminDashboard;
